import { useCallback, useEffect, useState } from 'react';

/**
 * PWA install action (full-live-cutover §3, Settings). Captures the
 * beforeinstallprompt event so the Settings screen can offer a real
 * "Install app" action. No service worker ships yet (AGENTS.md known gap) —
 * the hook reports honestly: if the event never fires (already installed,
 * unsupported browser, or the manifest criteria not met) `canInstall` stays
 * false and the caller renders the explanatory disabled state instead of a
 * button that promises an install it cannot perform.
 */
function usePwaInstall() {
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the mini-infobar; we own the install UX.
      event.preventDefault();
      setInstallEvent(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installEvent) return false;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setInstallEvent(null);
    return outcome === 'accepted';
  }, [installEvent]);

  return {
    canInstall: installEvent !== null,
    install
  };
}

export default usePwaInstall;
